import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { getTickets, getQueueLookup, QueueLookup } from '../services/api';
import { Ticket } from '../services/types';
import PageHeader from '../components/PageHeader';
import TicketDetailModal from '../components/TicketDetailModal';
import NewTicketModal from '../components/NewTicketModal';
import { useAuth } from '../contexts/AuthProvider';

function PriorityBadge({ priority }: { priority: string }) {
  const colorClasses: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${colorClasses[priority.toLowerCase()] || colorClasses.medium}`}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colorClasses: Record<string, string> = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'in progress': 'bg-green-500/20 text-green-400 border-green-500/30',
    'waiting customer': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'waiting on us': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    complete: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    closed: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };

  const normalizedStatus = status.toLowerCase();

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${colorClasses[normalizedStatus] || colorClasses.new}`}>
      {status}
    </span>
  );
}

// Get date from ticket
function getTicketDate(ticket: Ticket): string | undefined {
  return (ticket as any).createdDate || ticket.createDate;
}

// Format ticket age as "Xd Xh" or "Xh Xm"
function formatAge(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays}d ${remainingHours}h`;
  }
  if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m`;
  }
  return `${diffMins}m`;
}

// Filter icon
function FilterIcon({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`ml-1 p-0.5 rounded hover:bg-slate-600 ${active ? 'text-blue-400' : 'text-slate-500'}`}
      title="Filter"
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
    </button>
  );
}

// Text filter popup with autocomplete
interface TextFilterPopupProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  suggestions?: string[];
}

function TextFilterPopup({ value, onChange, onClose, suggestions }: TextFilterPopupProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filteredSuggestions = suggestions
    ? suggestions
        .filter(s => s.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 10)
    : [];

  useEffect(() => {
    setSelectedIndex(-1);
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
        onChange(filteredSuggestions[selectedIndex]);
        onClose();
      }
    }
  };

  return (
    <div className="bg-slate-700 border border-slate-600 rounded shadow-lg p-2 min-w-[200px]" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-600 focus:border-blue-500 focus:outline-none"
        placeholder="Type to filter..."
      />
      {filteredSuggestions.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto">
          {filteredSuggestions.map((s, index) => (
            <div
              key={s}
              className={`px-2 py-1 text-xs cursor-pointer rounded ${
                index === selectedIndex ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-600'
              }`}
              onClick={() => { onChange(s); onClose(); }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex justify-between">
        <button onClick={() => { onChange(''); onClose(); }} className="text-xs text-slate-400 hover:text-white">Clear</button>
        <button onClick={onClose} className="text-xs text-blue-400 hover:text-blue-300">Done</button>
      </div>
    </div>
  );
}

// Multi-select dropdown filter with include/exclude mode
interface MultiSelectFilter {
  values: string[];
  mode: 'include' | 'exclude';
}

const emptyMultiFilter: MultiSelectFilter = { values: [], mode: 'include' };

interface DropdownFilterPopupProps {
  filter: MultiSelectFilter;
  options: string[];
  onChange: (filter: MultiSelectFilter) => void;
  onClose: () => void;
}

function DropdownFilterPopup({ filter, options, onChange, onClose }: DropdownFilterPopupProps) {
  const { values, mode } = filter;

  const toggleValue = (opt: string) => {
    const next = values.includes(opt)
      ? values.filter(v => v !== opt)
      : [...values, opt];
    onChange({ values: next, mode });
  };

  const clearAll = () => {
    onChange(emptyMultiFilter);
    onClose();
  };

  return (
    <div
      className="bg-slate-700 border border-slate-600 rounded shadow-lg p-2 min-w-[170px]"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {/* Mode toggle */}
      <div className="flex items-center gap-1 mb-1.5 pb-1.5 border-b border-slate-600">
        <button
          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${mode === 'include' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-400 hover:text-slate-200'}`}
          onClick={() => onChange({ values, mode: 'include' })}
        >
          Include
        </button>
        <button
          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${mode === 'exclude' ? 'bg-red-600 text-white' : 'bg-slate-600 text-slate-400 hover:text-slate-200'}`}
          onClick={() => onChange({ values, mode: 'exclude' })}
        >
          Exclude
        </button>
        {values.length > 0 && (
          <button
            className="ml-auto text-[10px] text-slate-400 hover:text-white"
            onClick={clearAll}
          >
            Clear
          </button>
        )}
      </div>
      <div className="max-h-48 overflow-y-auto">
        {options.map(opt => {
          const selected = values.includes(opt);
          return (
            <div
              key={opt}
              className={`px-2 py-1 text-xs cursor-pointer rounded flex items-center gap-1.5 ${
                selected
                  ? (mode === 'exclude' ? 'bg-red-600/30 text-red-200' : 'bg-blue-600/30 text-blue-200')
                  : 'text-slate-300 hover:bg-slate-600'
              }`}
              onClick={() => toggleValue(opt)}
            >
              <span className={`w-3 h-3 flex-shrink-0 border rounded-sm flex items-center justify-center text-[9px] ${
                selected
                  ? (mode === 'exclude' ? 'border-red-500 bg-red-600 text-white' : 'border-blue-500 bg-blue-600 text-white')
                  : 'border-slate-500'
              }`}>
                {selected && (mode === 'exclude' ? '✕' : '✓')}
              </span>
              {opt}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Date filter popup
interface DateFilterPopupProps {
  dateFrom: string;
  dateTo: string;
  onChangeDateFrom: (value: string) => void;
  onChangeDateTo: (value: string) => void;
  onClose: () => void;
}

function DateFilterPopup({ dateFrom, dateTo, onChangeDateFrom, onChangeDateTo, onClose }: DateFilterPopupProps) {
  return (
    <div className="bg-slate-700 border border-slate-600 rounded shadow-lg p-2 min-w-[200px]" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
      <div className="space-y-2">
        <div>
          <label className="text-[10px] text-slate-400">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onChangeDateFrom(e.target.value)}
            className="w-full bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-600 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-400">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onChangeDateTo(e.target.value)}
            className="w-full bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-600 focus:border-blue-500"
          />
        </div>
      </div>
      <div className="mt-2 flex justify-between">
        <button onClick={() => { onChangeDateFrom(''); onChangeDateTo(''); onClose(); }} className="text-xs text-slate-400 hover:text-white">Clear</button>
        <button onClick={onClose} className="text-xs text-blue-400 hover:text-blue-300">Done</button>
      </div>
    </div>
  );
}

// Portal for filter popup
function FilterPortal({ children, anchorRef }: { children: React.ReactNode; anchorRef: React.RefObject<HTMLElement> }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const popupWidth = 220; // Estimated popup width
      const viewportWidth = window.innerWidth;

      // If popup would go off right edge, align to right side of anchor
      let left = rect.left;
      if (rect.left + popupWidth > viewportWidth - 20) {
        left = Math.max(20, rect.right - popupWidth);
      }

      setPosition({ top: rect.bottom + 4, left });
    }
  }, [anchorRef]);

  return createPortal(
    <div style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );
}

// Column filters interface
interface ColumnFilters {
  ticketNumber: string;
  title: string;
  client: string;
  contact: string;
  assigned: MultiSelectFilter;
  status: MultiSelectFilter;
  priority: MultiSelectFilter;
  queue: MultiSelectFilter;
  dateFrom: string;
  dateTo: string;
}

// Sortable/filterable header
type SortColumn = 'ticketNumber' | 'title' | 'client' | 'contact' | 'assigned' | 'status' | 'priority' | 'queue' | 'created';

interface SortConfig {
  column: SortColumn;
  direction: 'asc' | 'desc';
}

interface FilterableHeaderProps {
  label: string;
  columnKey: SortColumn;
  filterType: 'text' | 'dropdown' | 'date';
  value?: string;
  filter?: MultiSelectFilter;
  options?: string[];
  suggestions?: string[];
  dateFrom?: string;
  dateTo?: string;
  onChange?: (value: string) => void;
  onFilterChange?: (filter: MultiSelectFilter) => void;
  onChangeDateFrom?: (value: string) => void;
  onChangeDateTo?: (value: string) => void;
  sortConfigs: SortConfig[];
  onSort: (column: SortColumn, addToMulti: boolean) => void;
}

function FilterableHeader({
  label, columnKey, filterType, value, filter, options, suggestions,
  dateFrom, dateTo, onChange, onFilterChange, onChangeDateFrom, onChangeDateTo,
  sortConfigs, onSort
}: FilterableHeaderProps) {
  const [showFilter, setShowFilter] = useState(false);
  const headerRef = useRef<HTMLTableCellElement>(null);
  const sortConfig = sortConfigs.find(s => s.column === columnKey);

  const isFilterActive = filterType === 'date'
    ? Boolean(dateFrom || dateTo)
    : filterType === 'dropdown'
    ? Boolean(filter && filter.values.length > 0)
    : Boolean(value);

  useEffect(() => {
    if (!showFilter) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setShowFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilter]);

  const sortIndex = sortConfigs.findIndex(s => s.column === columnKey);

  return (
    <th ref={headerRef} className="px-3 py-2 relative">
      <div className="flex items-center">
        <span
          className="cursor-pointer select-none hover:text-white flex items-center gap-1"
          onClick={(e) => onSort(columnKey, e.shiftKey)}
        >
          {label}
          {/* Sort indicator - always visible */}
          <span className={`text-[10px] ${sortConfig ? 'text-blue-400' : 'text-slate-600'}`}>
            {sortConfig
              ? (sortConfig.direction === 'asc' ? '▲' : '▼')
              : '○'
            }
          </span>
          {/* Multi-sort order number */}
          {sortConfigs.length > 1 && sortIndex >= 0 && (
            <span className="text-[9px] bg-blue-600 text-white px-1 rounded">
              {sortIndex + 1}
            </span>
          )}
        </span>
        <FilterIcon active={isFilterActive} onClick={() => setShowFilter(!showFilter)} />
      </div>
      {showFilter && (
        <FilterPortal anchorRef={headerRef}>
          {filterType === 'text' && onChange && (
            <TextFilterPopup value={value || ''} onChange={onChange} onClose={() => setShowFilter(false)} suggestions={suggestions} />
          )}
          {filterType === 'dropdown' && options && onFilterChange && filter && (
            <DropdownFilterPopup filter={filter} options={options} onChange={onFilterChange} onClose={() => setShowFilter(false)} />
          )}
          {filterType === 'date' && onChangeDateFrom && onChangeDateTo && (
            <DateFilterPopup dateFrom={dateFrom || ''} dateTo={dateTo || ''} onChangeDateFrom={onChangeDateFrom} onChangeDateTo={onChangeDateTo} onClose={() => setShowFilter(false)} />
          )}
        </FilterPortal>
      )}
    </th>
  );
}

// Ticket row component
interface TicketRowProps {
  ticket: Ticket;
  onTitleClick: (ticket: Ticket) => void;
  resolveQueueName: (queue: { id: number; name: string } | undefined) => string;
}

function TicketRow({ ticket, onTitleClick, resolveQueueName }: TicketRowProps) {
  const [expanded, setExpanded] = useState(false);

  const handleRowClick = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <tr className="hover:bg-slate-700/30 transition-colors cursor-pointer" onClick={handleRowClick}>
        <td className="px-3 py-2 text-slate-400 text-xs">
          <span className={`transition-transform inline-block ${expanded ? 'rotate-90' : ''}`}>▶</span>
        </td>
        <td className="px-3 py-2">
          <a
            href={`https://ww6.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=${ticket.autoTaskId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-400 hover:text-blue-300 hover:underline font-mono text-xs"
            title="Open in AutoTask"
          >
            {ticket.ticketNumber}
          </a>
        </td>
        <td className="px-3 py-2 text-xs text-white truncate max-w-xs" title={ticket.title}>
          <button
            onClick={(e) => { e.stopPropagation(); onTitleClick(ticket); }}
            className="text-white hover:text-blue-300 hover:underline text-left truncate max-w-xs"
          >
            {ticket.title}
          </button>
        </td>
        <td className="px-3 py-2 text-xs text-slate-300">{ticket.companyName || '-'}</td>
        <td className="px-3 py-2 text-xs text-slate-400 truncate max-w-[160px]" title={ticket.contactEmail || ''}>{ticket.contactEmail || '-'}</td>
        <td className="px-3 py-2 text-xs">
          {ticket.assignedResourceName ? (
            <span className="text-slate-300">{ticket.assignedResourceName}</span>
          ) : (
            <span className="text-yellow-400">Unassigned</span>
          )}
        </td>
        <td className="px-3 py-2"><StatusBadge status={ticket.status} /></td>
        <td className="px-3 py-2"><PriorityBadge priority={ticket.priority} /></td>
        <td className="px-3 py-2 text-xs text-slate-400 truncate max-w-[100px]" title={resolveQueueName(ticket.queue)}>{resolveQueueName(ticket.queue)}</td>
        <td className="px-3 py-2 text-xs text-slate-400 font-mono">{formatAge(getTicketDate(ticket))}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-700/20">
          <td colSpan={10} className="px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <div className="text-slate-500 text-[10px]">Queue</div>
                <div className="text-white">{resolveQueueName(ticket.queue)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[10px]">Created</div>
                <div className="text-white">
                  {getTicketDate(ticket) ? format(new Date(getTicketDate(ticket)!), 'MMM d, yyyy h:mm a') : '-'}
                </div>
              </div>
              <div>
                <div className="text-slate-500 text-[10px]">Hours</div>
                <div className="text-white">{ticket.hoursWorked || 0}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[10px]">Last Activity</div>
                <div className="text-white">
                  {ticket.lastActivityDate ? format(new Date(ticket.lastActivityDate), 'MMM d, yyyy h:mm a') : '-'}
                </div>
              </div>
            </div>
            {ticket.description && (
              <div className="mt-3">
                <div className="text-slate-500 text-[10px] mb-1">Description</div>
                <div className="text-slate-300 text-xs whitespace-pre-wrap bg-slate-800 p-2 rounded max-h-32 overflow-y-auto">
                  {ticket.description}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function Tickets() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Load queue lookup from cache first, then refresh from API
  const [queueLookup, setQueueLookup] = useState<QueueLookup>(() => {
    try {
      const cached = localStorage.getItem('queue-lookup');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  // Map URL filter param to status value
  const getInitialFilters = (): { status: string; showUnassignedOnly: boolean } => {
    const filter = searchParams.get('filter');
    const filterMap: Record<string, string> = {
      'in-progress': 'In Progress',
      'waiting-customer': 'Waiting Customer',
      'waiting-us': 'Waiting On Us',
    };
    return {
      status: filterMap[filter || ''] || '',
      showUnassignedOnly: filter === 'new',
    };
  };

  // Special filter for "new" (unassigned) tickets
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(() => getInitialFilters().showUnassignedOnly);

  // Special filter for merged tickets
  const [showMergedOnly, setShowMergedOnly] = useState(false);

  // Column filters
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(() => {
    const initialStatus = getInitialFilters().status;
    return {
      ticketNumber: '',
      title: '',
      client: searchParams.get('client') || '',
      contact: '',
      assigned: { ...emptyMultiFilter },
      status: initialStatus ? { values: [initialStatus], mode: 'include' as const } : { ...emptyMultiFilter },
      priority: { ...emptyMultiFilter },
      queue: { ...emptyMultiFilter },
      dateFrom: '',
      dateTo: '',
    };
  });

  // Update filters when URL param changes
  useEffect(() => {
    const filter = searchParams.get('filter');
    const clientParam = searchParams.get('client');
    const filterMap: Record<string, string> = {
      'in-progress': 'In Progress',
      'waiting-customer': 'Waiting Customer',
      'waiting-us': 'Waiting On Us',
    };
    const statusValue = filterMap[filter || ''] || '';
    setShowUnassignedOnly(filter === 'new');
    if (statusValue) {
      setColumnFilters(prev => ({ ...prev, status: { values: [statusValue], mode: 'include' as const } }));
    } else if (filter === 'new') {
      // Clear status filter when showing unassigned
      setColumnFilters(prev => ({ ...prev, status: { ...emptyMultiFilter } }));
    }
    // Pre-fill client filter from URL param (e.g. from Field Map click)
    if (clientParam) {
      setColumnFilters(prev => ({ ...prev, client: clientParam }));
    }
  }, [searchParams]);

  // Sorting - default: newest tickets first (smallest age at top)
  const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([{ column: 'created', direction: 'desc' }]);

  // Modal
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  // Helper to resolve queue name from ID
  const resolveQueueName = (queue: { id: number; name: string } | undefined): string => {
    if (!queue) return '-';
    // If name looks like "Queue 12345", try to resolve from lookup
    if (queue.name.match(/^Queue \d+$/)) {
      const resolvedName = queueLookup[queue.id] || queueLookup[String(queue.id)];
      return resolvedName || queue.name;
    }
    return queue.name;
  };

  // Fetch queue lookup on mount and cache it
  useEffect(() => {
    getQueueLookup()
      .then(lookup => {
        setQueueLookup(lookup);
        localStorage.setItem('queue-lookup', JSON.stringify(lookup));
      })
      .catch(err => console.error('Failed to load queue lookup:', err));
  }, []);

  // Fetch tickets - use LIVE endpoint when filtering by status (for accuracy)
  useEffect(() => {
    async function fetchTickets() {
      try {
        setLoading(true);

        const filter = searchParams.get('filter');

        // Map URL filter to status value for live query
        const filterToStatus: Record<string, string> = {
          'new': 'New',
          'in-progress': 'In Progress',
          'waiting-customer': 'Waiting Customer',
          'waiting-us': 'Waiting On Us',
        };

        const statusForLive = filterToStatus[filter || ''];

        if (statusForLive || filter === 'new') {
          // Fetch from AutoTask LIVE for status-filtered views (always accurate)
          console.log(`Fetching LIVE tickets for filter: ${filter}`);
          const liveData = await getTickets({
            status: statusForLive || 'New',
            unassigned: filter === 'new',
            live: true,
          });

          // Also fetch all tickets for non-filtered views
          const allData = await getTickets({ limit: 2000 });

          // Merge: replace matching tickets with live data
          const liveIds = new Set(liveData.map(t => t.id));
          const merged = [
            ...liveData,
            ...allData.filter(t => !liveIds.has(t.id)),
          ];

          setTickets(merged);
        } else {
          // No status filter - use cached data
          const data = await getTickets({ limit: 2000 });
          setTickets(data);
        }

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    }

    fetchTickets();
  }, [searchParams]);

  // Handle sort
  const handleSort = (column: SortColumn, addToMulti: boolean) => {
    setSortConfigs(prev => {
      const existingIndex = prev.findIndex(s => s.column === column);
      if (existingIndex >= 0) {
        const existing = prev[existingIndex];
        if (existing.direction === 'asc') {
          const updated = [...prev];
          updated[existingIndex] = { ...existing, direction: 'desc' };
          return updated;
        } else {
          return prev.filter((_, i) => i !== existingIndex);
        }
      } else {
        const newSort: SortConfig = { column, direction: 'asc' };
        return addToMulti ? [...prev, newSort] : [newSort];
      }
    });
  };

  // Get unique values for dropdowns
  const statusOptions = [...new Set(tickets.map(t => t.status).filter(Boolean))].sort();
  const priorityOptions = ['Critical', 'High', 'Medium', 'Low'];
  const clientOptions = [...new Set(tickets.map(t => t.companyName).filter((n): n is string => Boolean(n) && !/^Company \d+$/.test(n as string)))].sort();
  const techOptions = [...new Set(tickets.map(t => t.assignedResourceName).filter((n): n is string => Boolean(n)))].sort();
  // Resolve queue names for dropdown (use lookup if available)
  const queueOptions = [...new Set(tickets.map(t => resolveQueueName(t.queue)).filter((n): n is string => Boolean(n) && n !== '-'))].sort();

  // Apply filters
  const applyFilters = (ticketList: Ticket[]): Ticket[] => {
    return ticketList.filter(ticket => {
      // Special filter for unassigned tickets (from URL ?filter=new)
      if (showUnassignedOnly && ticket.assignedResourceName) return false;

      // Special filter for merged tickets
      if (showMergedOnly && !ticket.mergedInto) return false;

      // Text filters
      if (columnFilters.ticketNumber && !ticket.ticketNumber?.toLowerCase().includes(columnFilters.ticketNumber.toLowerCase())) return false;
      if (columnFilters.title && !ticket.title?.toLowerCase().includes(columnFilters.title.toLowerCase())) return false;
      if (columnFilters.client && !ticket.companyName?.toLowerCase().includes(columnFilters.client.toLowerCase())) return false;
      if (columnFilters.contact && !(ticket.contactEmail || '').toLowerCase().includes(columnFilters.contact.toLowerCase())) return false;

      // Multi-select dropdown filters
      if (columnFilters.assigned.values.length > 0) {
        const match = columnFilters.assigned.values.includes(ticket.assignedResourceName || '');
        if (columnFilters.assigned.mode === 'include' && !match) return false;
        if (columnFilters.assigned.mode === 'exclude' && match) return false;
      }
      if (columnFilters.status.values.length > 0) {
        const match = columnFilters.status.values.includes(ticket.status || '');
        if (columnFilters.status.mode === 'include' && !match) return false;
        if (columnFilters.status.mode === 'exclude' && match) return false;
      }
      if (columnFilters.priority.values.length > 0) {
        const match = columnFilters.priority.values.includes(ticket.priority || '');
        if (columnFilters.priority.mode === 'include' && !match) return false;
        if (columnFilters.priority.mode === 'exclude' && match) return false;
      }
      if (columnFilters.queue.values.length > 0) {
        const match = columnFilters.queue.values.includes(resolveQueueName(ticket.queue) || '');
        if (columnFilters.queue.mode === 'include' && !match) return false;
        if (columnFilters.queue.mode === 'exclude' && match) return false;
      }

      // Date filter
      if (columnFilters.dateFrom || columnFilters.dateTo) {
        const ticketDate = getTicketDate(ticket);
        if (!ticketDate) return false;
        const date = new Date(ticketDate);
        if (columnFilters.dateFrom && date < new Date(columnFilters.dateFrom)) return false;
        if (columnFilters.dateTo && date > new Date(columnFilters.dateTo + 'T23:59:59')) return false;
      }

      return true;
    });
  };

  // Apply sorting
  const applySorting = (ticketList: Ticket[]): Ticket[] => {
    if (sortConfigs.length === 0) {
      // Default: sort by created date descending
      return [...ticketList].sort((a, b) => {
        const dateA = new Date(getTicketDate(a) || 0).getTime();
        const dateB = new Date(getTicketDate(b) || 0).getTime();
        return dateB - dateA;
      });
    }

    return [...ticketList].sort((a, b) => {
      for (const config of sortConfigs) {
        let aVal: any, bVal: any;
        switch (config.column) {
          case 'ticketNumber': aVal = a.ticketNumber || ''; bVal = b.ticketNumber || ''; break;
          case 'title': aVal = a.title?.toLowerCase() || ''; bVal = b.title?.toLowerCase() || ''; break;
          case 'client': aVal = a.companyName?.toLowerCase() || ''; bVal = b.companyName?.toLowerCase() || ''; break;
          case 'contact': aVal = (a.contactEmail || '').toLowerCase(); bVal = (b.contactEmail || '').toLowerCase(); break;
          case 'assigned': aVal = a.assignedResourceName?.toLowerCase() || ''; bVal = b.assignedResourceName?.toLowerCase() || ''; break;
          case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
          case 'priority':
            const pOrder: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
            aVal = pOrder[a.priority] ?? 99;
            bVal = pOrder[b.priority] ?? 99;
            break;
          case 'queue':
            aVal = resolveQueueName(a.queue).toLowerCase();
            bVal = resolveQueueName(b.queue).toLowerCase();
            break;
          case 'created':
            aVal = new Date(getTicketDate(a) || 0).getTime();
            bVal = new Date(getTicketDate(b) || 0).getTime();
            break;
        }
        if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredTickets = applySorting(applyFilters(tickets));

  const hasColumnFilters = Boolean(
    columnFilters.ticketNumber || columnFilters.title || columnFilters.client || columnFilters.contact ||
    columnFilters.dateFrom || columnFilters.dateTo ||
    columnFilters.assigned.values.length || columnFilters.status.values.length ||
    columnFilters.priority.values.length || columnFilters.queue.values.length
  ) || showUnassignedOnly || showMergedOnly;

  // Check if any filters/sort are non-default
  const hasAnyFilters = hasColumnFilters ||
    sortConfigs.length !== 1 ||
    sortConfigs[0]?.column !== 'created' ||
    sortConfigs[0]?.direction !== 'desc';

  const clearColumnFilters = () => {
    setColumnFilters({
      ticketNumber: '', title: '', client: '', contact: '',
      assigned: { ...emptyMultiFilter }, status: { ...emptyMultiFilter },
      priority: { ...emptyMultiFilter }, queue: { ...emptyMultiFilter },
      dateFrom: '', dateTo: '',
    });
    setShowUnassignedOnly(false);
    setShowMergedOnly(false);
  };

  // Reset everything to defaults
  const resetToDefaults = () => {
    clearColumnFilters();
    setSortConfigs([{ column: 'created', direction: 'desc' }]);
  };

  const handleTitleClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  const refreshTickets = async () => {
    try {
      const data = await getTickets({ limit: 2000 });
      setTickets(data);
    } catch (err) {
      console.error('Failed to refresh tickets:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading tickets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        userEmail={user?.email}
        onNewTicket={() => setShowNewTicketModal(true)}
        middleContent={
          <div className="text-center">
            <span className="text-sm font-medium text-white">All Tickets</span>
            <span className="text-xs text-slate-500 ml-2">{filteredTickets.length} of {tickets.length}</span>
          </div>
        }
      />

      {/* Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden mt-4">
        {/* Table hints */}
        <div className="px-4 py-2 border-b border-slate-700 bg-slate-700/50 flex justify-between items-center">
          <span className="text-[10px] text-slate-500">
            Click ticket # to edit • Click row to expand • Shift+click column to multi-sort
          </span>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showMergedOnly}
                onChange={(e) => setShowMergedOnly(e.target.checked)}
                className="w-3 h-3 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              <span className="text-xs text-slate-400">Show Merged Only</span>
            </label>
            {hasAnyFilters && (
              <button onClick={resetToDefaults} className="text-xs text-blue-400 hover:text-blue-300">
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {filteredTickets.length === 0 ? (
          <div className="p-4 text-slate-400 text-sm text-center">No tickets found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700 text-left text-xs text-slate-400">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <FilterableHeader
                    label="Ticket #"
                    columnKey="ticketNumber"
                    filterType="text"
                    value={columnFilters.ticketNumber}
                    onChange={(v) => setColumnFilters(prev => ({ ...prev, ticketNumber: v }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Title"
                    columnKey="title"
                    filterType="text"
                    value={columnFilters.title}
                    onChange={(v) => setColumnFilters(prev => ({ ...prev, title: v }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Client"
                    columnKey="client"
                    filterType="text"
                    value={columnFilters.client}
                    suggestions={clientOptions}
                    onChange={(v) => setColumnFilters(prev => ({ ...prev, client: v }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Contact"
                    columnKey="contact"
                    filterType="text"
                    value={columnFilters.contact}
                    onChange={(v) => setColumnFilters(prev => ({ ...prev, contact: v }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Assigned"
                    columnKey="assigned"
                    filterType="dropdown"
                    filter={columnFilters.assigned}
                    options={techOptions}
                    onFilterChange={(f) => setColumnFilters(prev => ({ ...prev, assigned: f }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Status"
                    columnKey="status"
                    filterType="dropdown"
                    filter={columnFilters.status}
                    options={statusOptions}
                    onFilterChange={(f) => setColumnFilters(prev => ({ ...prev, status: f }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Priority"
                    columnKey="priority"
                    filterType="dropdown"
                    filter={columnFilters.priority}
                    options={priorityOptions}
                    onFilterChange={(f) => setColumnFilters(prev => ({ ...prev, priority: f }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Queue"
                    columnKey="queue"
                    filterType="dropdown"
                    filter={columnFilters.queue}
                    options={queueOptions}
                    onFilterChange={(f) => setColumnFilters(prev => ({ ...prev, queue: f }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                  <FilterableHeader
                    label="Age"
                    columnKey="created"
                    filterType="date"
                    dateFrom={columnFilters.dateFrom}
                    dateTo={columnFilters.dateTo}
                    onChangeDateFrom={(v) => setColumnFilters(prev => ({ ...prev, dateFrom: v }))}
                    onChangeDateTo={(v) => setColumnFilters(prev => ({ ...prev, dateTo: v }))}
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredTickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    onTitleClick={handleTitleClick}
                    resolveQueueName={resolveQueueName}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={showTicketModal}
        onClose={() => {
          setShowTicketModal(false);
          setSelectedTicket(null);
        }}
        onTicketUpdated={refreshTickets}
      />

      {/* New Ticket Modal */}
      <NewTicketModal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onTicketCreated={() => refreshTickets()}
      />
    </div>
  );
}
